import React, { useState, useContext, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { injectIntl, IntlShape } from 'react-intl'
import { Link, useLocation, useRouteMatch } from 'react-router-dom'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { selectCurrentUser, selectCurrentZone, selectAvailableZones, setCurrentZone } from 'store/global'
import styles from './Header.scss'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { exportSingleEntityFile } from 'services/verticalsApi'
import { generateExcelFromData } from 'utils/generateExcelFromData'
import { generateCompleteFileName } from 'utils/fileNameGenerator'
import { VerticalContext } from 'store/verticals/Context'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import { Avatar, Button as AntButton, Divider, Popover, Select, Tooltip, Typography } from 'antd'
import { handleLogout } from './utils'
import localStorageService from 'services/storage'
import history from 'utils/history'
import { useCopyToClipboard } from 'uikit/hooks/use-copy-to-clipboard'
import CurrencySwitcher from 'components/CurrencySwitcher'
import { useCurrency } from 'contexts'
import { CheckOutlined, CopyOutlined } from '@ant-design/icons'

interface HeaderProps {
  intl: IntlShape
  currentZoneIndexes: {
    [key: string]: any
  }
}

interface RouteParams {
  vertical: string
  entityId: string
}

function stringToColor(string: string) {
  let hash = 0
  let i

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash)
  }

  let color = '#'

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff
    color += `00${value.toString(16)}`.slice(-2)
  }
  /* eslint-enable no-bitwise */

  return color
}

function stringAvatar(name: string) {
  const nameParts = name.split(' ')
  const initials = nameParts.length > 1 ? `${nameParts[0][0]}${nameParts[1][0]}` : nameParts[0][0]

  return {
    style: {
      backgroundColor: stringToColor(name),
      width: 24,
      height: 24,
      fontSize: '12px',
      cursor: 'pointer',
    },
    children: initials.toUpperCase(),
  }
}

function Header({ intl, currentZoneIndexes }: HeaderProps) {
  const location = useLocation()
  const dispatch = useDispatch()
  const { trackEvent } = useMatomo()
  const { getVerticalEntity, verticalEntity } = useContext(VerticalContext)

  const match = useRouteMatch<RouteParams>('/vertical/:vertical/:entityId')

  const [loading, setLoading] = useState(false)
  const { currentCurrency, setCurrentCurrency, exchangeRates, isLoadingExchangeRates } = useCurrency()
  const currentZone = useSelector(selectCurrentZone)
  const availableZones = useSelector(selectAvailableZones)
  const currentUser = useSelector(selectCurrentUser)
  const { copied, copy } = useCopyToClipboard()
  const rawBuildTag = (window as any)?._env_?.BUILD_TAG

  const handleCopy = () => {
    copy(rawBuildTag)
  }

  const sortedZones = useMemo(() => {
    return [...availableZones].sort((a, b) => a.name.localeCompare(b.name))
  }, [availableZones])

  useEffect(() => {
    if (!match?.params?.entityId || !currentZoneIndexes?.baseIndices) return

    const fetchEntityData = async () => {
      await getVerticalEntity(match.params.entityId, currentZoneIndexes.baseIndices)
    }
    fetchEntityData()
  }, [currentZoneIndexes?.baseIndices])

  const { email = '', fullName = '' } = currentUser || {}

  const { name = '' } = currentZone || {}
  const currentZoneName = name

  const onZoneSelect = (zoneId: string) => {
    const nextZoneId = parseInt(zoneId)
    const nextZone = sortedZones.find(({ id }: { id: number }) => id === nextZoneId)

    if (nextZone) {
      dispatch(setCurrentZone({ zone: nextZone }))
      localStorageService.setLastSelectedZoneForUser(currentUser, nextZone)
      history.push('/dashboard')
    }
  }

  const onXLSXExport = async () => {
    if (!match?.params) return

    setLoading(true)
    try {
      const entityId = match.params.entityId
      const verticalType = match.params.vertical
      const csvTypes = {
        baseCsvType: `single-entity-${verticalType}`,
        activityCsvType: `activity-${verticalType}`,
      }

      const tin = verticalEntity?.entityDetails?.tin
      const onlineName = verticalEntity?.revenueSources?.[0]?.profileName || verticalEntity?.entityDetails?.name

      const currencyContext = {
        currencyConversion: currentCurrency !== 'USD',
        currentCurrency,
        exchangeRates: exchangeRates?.[`${currentCurrency}_all_yearly`] || null,
      }

      const exportData = await exportSingleEntityFile(
        entityId,
        currentZoneIndexes,
        csvTypes,
        verticalType,
        currencyContext,
      )

      const fileName = generateCompleteFileName({
        vertical: verticalType,
        tin,
        onlineName,
        entityId,
        exportType: 'single-entity',
      })

      generateExcelFromData(exportData, fileName, currencyContext)
      trackEvent({ category: 'Export', action: 'User requested for single entity XLSX' })
    } catch (error) {
      console.error('Export failed', error)
    } finally {
      setLoading(false)
    }
  }

  const accountContent = (
    <div style={{ minWidth: 200 }}>
      <Typography.Title level={5} style={{ margin: '0' }}>
        Account
      </Typography.Title>
      <Divider style={{ margin: '5px 0 16px' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <Avatar {...stringAvatar(fullName || 'User')} />
        <div>
          <div style={{ fontSize: 14 }}>{fullName || 'User'}</div>
          <div style={{ fontSize: 12 }}>{email}</div>
        </div>
      </div>
      <Divider style={{ margin: '10px 0 12px' }} />
      {rawBuildTag && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <Tooltip title={rawBuildTag || ''}>
              <Typography.Text
                style={{
                  fontSize: 12,
                  maxWidth: 160,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Version: {rawBuildTag}
              </Typography.Text>
            </Tooltip>
            <AntButton
              type='text'
              size='small'
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              onClick={handleCopy}
              style={{ color: copied ? '#52c41a' : 'inherit' }}
            />
          </div>
          <Divider style={{ margin: '10px 0 12px' }} />
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={handleLogout}>
        <span
          style={{
            fontWeight: 400,
            fontSize: '14px',
          }}
        >
          Log Out
        </span>
        <AntButton size='small' ghost>
          <Icon name='logout' size='regular' />
        </AntButton>
      </div>
    </div>
  )

  return (
    <>
      <Backdrop open={loading} style={{ zIndex: 1300, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <header className={styles.header}>
        <Link to='/dashboard' color='link' className={styles.logoLink} title='IVIX'>
          <Icon name='logo' size='xl' className={styles.logo} />
        </Link>
        <div className={styles.linksWrapper}>
          <ul className={styles.linksList}>
            <li className={styles.linkItem}>
              <Popover
                content={accountContent}
                placement='bottomLeft'
                trigger='click'
                styles={{
                  body: {
                    boxShadow: '0px 2px 8px 0px #00000026',
                    borderRadius: 2,
                    padding: '6px 12px',
                  },
                }}
              >
                <Button buttonType='transparent' to={undefined} className={styles.profile}>
                  <Avatar {...stringAvatar(fullName || 'User')} />
                </Button>
              </Popover>
            </li>
            <Divider type='vertical' style={{ height: 32, margin: '0 4px', background: '#CAC4D0' }} />
            <li className={styles.linkItem}>
              {currentZoneName && availableZones.length > 1 && (
                <div className={styles.currentZone}>
                  <Select
                    value={currentZone?.id?.toString()}
                    onChange={onZoneSelect}
                    style={{ minWidth: 120 }}
                    size='small'
                    showSearch
                    placeholder='Select zone'
                    optionFilterProp='children'
                    filterOption={(input, option) =>
                      String(option?.children || '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {sortedZones.map((zone: { id: number; name: string }) => (
                      <Select.Option key={zone.id} value={zone.id.toString()}>
                        {zone.name}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </li>
            <li className={styles.linkItem}>
              <CurrencySwitcher currentCurrency={currentCurrency} onCurrencyChange={setCurrentCurrency} />
            </li>
            {location.pathname.includes('/vertical') && !location.pathname.includes('management') && (
              <li>
                <Button
                  buttonType='secondary'
                  className={styles.export}
                  disabled={loading || isLoadingExchangeRates}
                  onClick={onXLSXExport}
                >
                  <Icon name='exportFile' size='regular' />
                  {isLoadingExchangeRates ? 'Loading...' : 'EXPORT'}
                </Button>
              </li>
            )}
          </ul>
        </div>
      </header>
    </>
  )
}

export default injectIntl(Header)
